/**
 * Web Push subscription helper.
 *
 * Backs the "Browser notifications" option on the alert form: a second
 * delivery channel alongside Telegram. The form (owned by another file)
 * imports these named functions to gate the toggle on eligibility and to
 * subscribe / unsubscribe the current device. No i18n lives here - the
 * caller passes any display strings.
 *
 * Every function is SSR-safe: anything that touches window / navigator /
 * Notification guards `typeof window === "undefined"` first, so importing
 * this module from a server component is harmless.
 *
 * Talks to the backend at:
 *   GET  /alerts/push/key          -> { enabled, public_key }
 *   POST /alerts/push/subscribe    <- PushSubscription.toJSON()
 *   POST /alerts/push/unsubscribe  <- { endpoint }
 * using the same { credentials: "include" } style as lib/api.ts.
 */
import { API_BASE } from "@/lib/api";

const SW_URL = "/sw.js";

/** Convert the base64url VAPID public key the backend returns into the
 *  Uint8Array the Push API's applicationServerKey expects. Standard
 *  helper from the Web Push spec examples. */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  // Back the array with a concrete ArrayBuffer (not ArrayBufferLike) so
  // it satisfies BufferSource for pushManager.subscribe's
  // applicationServerKey under newer TS lib typings.
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** iPhone/iPad Safari that is NOT an installed standalone PWA. iOS only
 *  delivers web push to home-screen-installed PWAs, so a plain Safari
 *  tab on iOS is NOT eligible - the toggle must be hidden there. */
export function isIosNonStandalone(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  const ua = navigator.userAgent || "";
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports as Mac; disambiguate via touch points.
    (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1);
  if (!isIos) return false;

  const nav = navigator as Navigator & { standalone?: boolean };
  const standalone =
    nav.standalone === true ||
    (typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches);
  return !standalone;
}

/** Does this browser have the APIs web push needs at all? */
export function isPushSupported(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** The gate the form uses: supported AND not an iOS non-standalone tab.
 *  When false, the form should not show the browser-notifications option
 *  at all. */
export function isPushEligible(): boolean {
  return isPushSupported() && !isIosNonStandalone();
}

/** Register the service worker (reusing an existing registration when
 *  present). Returns null off-browser / when SWs are unsupported. */
async function getOrRegisterSW(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_URL);
}

/**
 * Fetch the VAPID public key from ``keyPath`` and return THIS browser's
 * push subscription, creating it (permission prompt included) when it
 * doesn't exist yet. Persists NOTHING server-side - the caller decides
 * which backend endpoint the subscription belongs to (the alert form
 * posts to /alerts/push/subscribe; the residence follow flow bundles it
 * into POST /follows/residence).
 *
 * keyPath default stays the auth-gated alerts one; the follow flow
 * passes "/follows/config", which needs no session.
 */
export async function getOrCreatePushSubscription(
  keyPath = "/alerts/push/key",
): Promise<{ sub: PushSubscription | null; reason?: string }> {
  if (!isPushSupported()) return { sub: null, reason: "unsupported" };

  // 1. Is push configured on the backend?
  let publicKey = "";
  try {
    const res = await fetch(`${API_BASE}${keyPath}`, {
      credentials: "include",
    });
    if (!res.ok) return { sub: null, reason: "error" };
    const data = (await res.json()) as { enabled?: boolean; public_key?: string };
    if (!data.enabled || !data.public_key) {
      return { sub: null, reason: "disabled" };
    }
    publicKey = data.public_key;
  } catch {
    return { sub: null, reason: "error" };
  }

  // 2. Permission. requestPermission resolves to the final state.
  let permission: NotificationPermission;
  try {
    permission = await Notification.requestPermission();
  } catch {
    return { sub: null, reason: "error" };
  }
  if (permission !== "granted") {
    return { sub: null, reason: "denied" };
  }

  // 3. SW + PushManager subscription.
  try {
    const reg = await getOrRegisterSW();
    if (!reg) return { sub: null, reason: "unsupported" };
    await navigator.serviceWorker.ready;

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }
    return { sub };
  } catch {
    return { sub: null, reason: "error" };
  }
}

/** Endpoint URL of this browser's existing push subscription, or null
 *  when the device never subscribed. Never prompts. SSR-safe. */
export async function getCurrentPushEndpoint(): Promise<string | null> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return null;
    const sub = await reg.pushManager.getSubscription();
    return sub ? sub.endpoint : null;
  } catch {
    return null;
  }
}

/**
 * Subscribe THIS browser to web push and persist the subscription on the
 * backend (the alert form's flow - requires a session).
 *
 * Returns { ok: true } on success, or { ok: false, reason } where reason
 * is one of: "unsupported" | "disabled" | "denied" | "error". The caller
 * maps these to its own localized messages.
 */
export async function subscribeToPush(): Promise<{ ok: boolean; reason?: string }> {
  const { sub, reason } = await getOrCreatePushSubscription();
  if (!sub) return { ok: false, reason: reason ?? "error" };
  try {
    const res = await fetch(`${API_BASE}/alerts/push/subscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(sub.toJSON()),
    });
    if (!res.ok) return { ok: false, reason: "error" };
    return { ok: true };
  } catch {
    return { ok: false, reason: "error" };
  }
}

/**
 * Unsubscribe THIS browser: drop the local PushManager subscription and
 * tell the backend to delete the stored row. Best-effort and idempotent;
 * never throws.
 */
export async function unsubscribeFromPush(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const endpoint = sub.endpoint;
    // Unsubscribe locally first so the browser stops receiving even if
    // the backend call fails.
    await sub.unsubscribe();
    await fetch(`${API_BASE}/alerts/push/unsubscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ endpoint }),
    });
  } catch {
    /* best-effort - a left-over backend row is pruned on its next 410 */
  }
}

/** Is this browser currently subscribed (has a live PushManager
 *  subscription)? Drives the toggle's on/off state. SSR-safe. */
export async function isThisDeviceSubscribed(): Promise<boolean> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return false;
  }
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return sub !== null;
  } catch {
    return false;
  }
}
